import React, { useState, useEffect, useCallback, memo } from "react";
import { Card, Row, Col, List, Button, Modal, Form, Select, InputNumber, Switch, Checkbox, Input, Tag, Empty, message } from "antd";
import { PlusOutlined, MinusCircleOutlined } from "@ant-design/icons";
import {
  getGroups,
  getContractsByGroup,
  getFacilitiesByGroup,
  getContractPeriodicals,
  getContractFacilityPeriodicals,
  upsertContractFacilityPeriodicals,
  isWorkingOnPeriodical,
  removeFacilityPeriodical,
  getOccurrences,
  getReportingTypes,
  getChargeTypes,
} from "./periodicalApi";

const { Option } = Select;
const { confirm } = Modal;

// Mirrors the old app's PeriodicalConfig/index.js layout: a "Contract
// Periodicals" panel on the left and a "Frequency Master" grid on the right
// (one cell per occurrence type, showing the periodicals assigned to it) —
// confirmed by reading that container's render() directly. The only
// deliberate change is the interaction: the old screen dragged a periodical
// card onto a frequency cell; here, clicking a Contract Periodicals row or
// an assigned tag opens an Assign/Edit modal instead (no drag library
// anywhere in this codebase). There's no diff endpoint: "available" = the
// contract's full catalogue (getContractPeriodicals) minus periodical_ids
// already present in getContractFacilityPeriodicals (the "assigned" list,
// grouped into grid cells by occurrence_id). The write endpoint (PUT
// managed_contract_facility_periodicals) is a per-item upsert — id > 0
// updates that mp008 row, no id inserts a new one — confirmed by reading
// ContractPeriodicalController.js directly — so Assign and Edit both go
// through the same call, just with/without an existing id.
// Shows a form value as plain text (keeps it in the form so it's still saved).
const ReadOnlyValue = ({ value, format }) => <div>{format ? format(value) : value ?? "-"}</div>;

// Same fields as the old "Edit Contract Periodical" dialog: name, description,
// frequency and occurrence count are shown read-only; notes, charging and
// reporting fields can be changed. The occurrence count always follows the
// frequency (its occur_frequency), and estimated hours are sent as 0, as the
// old app did. When assigning, the frequency picker takes the place of the
// old drag onto a frequency column.
const AssignModal = ({ visible, catalogRow, editingRow, occurrences, chargeTypes, reportingTypes, contractFacilityId, facilityName, onClose, onSaved }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const source = editingRow || catalogRow;

  useEffect(() => {
    if (!visible) return;
    form.setFieldsValue({
      frequency_type_id: source?.occurrence_id,
      is_seperately_charge: !!(editingRow ? editingRow.is_separately_charge : source?.is_separately_charge),
      charge_type_id: source?.charge_type_id,
      charge_amount: source?.charge_amount,
      notes: editingRow?.notes || "",
      reporting_fields: editingRow?.reporting_fields?.map((f) => ({ field_id: f.field_id, label: f.label, data_type_id: f.data_type_id, is_mandatory: !!f.is_mandatory })) || [],
    });
  }, [visible, editingRow, source, form]);

  const occurrenceCount = (frequencyId) => {
    const frequency = occurrences.find((o) => o.id === frequencyId);
    if (frequency?.occur_frequency) return frequency.occur_frequency;
    return frequencyId === source?.occurrence_id ? source?.frequency_count : undefined;
  };
  const frequencyName = (frequencyId) => occurrences.find((o) => o.id === frequencyId)?.name || "-";
  const label = { marginBottom: 4, fontSize: 13, color: "#5B6472" };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const item = {
        ...(editingRow ? { id: editingRow.id } : {}),
        contract_periodical_id: editingRow ? editingRow.contract_periodical_id : catalogRow.id,
        periodical_id: editingRow ? editingRow.periodical_id : catalogRow.periodical_id,
        contract_facility_id: contractFacilityId,
        contract_facility_name: facilityName,
        frequency_type_id: values.frequency_type_id,
        frequency_count: occurrenceCount(values.frequency_type_id) || 1,
        estimated_hours_per_occurrence: 0,
        is_seperately_charge: values.is_seperately_charge ? 1 : 0,
        charge_type_id: values.is_seperately_charge ? values.charge_type_id : null,
        charge_amount: values.is_seperately_charge ? values.charge_amount : null,
        notes: values.notes || "",
        reporting_fields: values.reporting_fields || [],
      };
      const { error, result } = await upsertContractFacilityPeriodicals([item]);
      if (error) message.error(result?.message || "Failed to save.");
      else {
        message.success(editingRow ? "Assignment updated." : "Periodical assigned.");
        onSaved();
        onClose();
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={editingRow ? "Edit Contract Periodical" : "Assign Contract Periodical"}
      visible={visible}
      onCancel={onClose}
      onOk={handleOk}
      okText="Save & Close"
      confirmLoading={saving}
      destroyOnClose
      width={600}
    >
      <div style={{ marginBottom: 16 }}>
        <div style={label}>Name</div>
        <div style={{ fontWeight: 600 }}>{source?.name}</div>
      </div>
      <div style={{ marginBottom: 16 }}>
        <div style={label}>Description</div>
        <div>{source?.description || "-"}</div>
      </div>
      <Form form={form} layout="vertical">
        <Row gutter={12}>
          <Col span={12}>
            {editingRow ? (
              <Form.Item name="frequency_type_id" label="Default Frequency" style={{ marginBottom: 16 }}>
                <ReadOnlyValue format={frequencyName} />
              </Form.Item>
            ) : (
              <Form.Item name="frequency_type_id" label="Frequency" rules={[{ required: true, message: "Please select a frequency." }]}>
                <Select showSearch optionFilterProp="children" placeholder="Select a frequency">
                  {occurrences.map((o) => <Option key={o.id} value={o.id}>{o.name}</Option>)}
                </Select>
              </Form.Item>
            )}
          </Col>
          <Col span={12}>
            <Form.Item noStyle shouldUpdate={(prev, cur) => prev.frequency_type_id !== cur.frequency_type_id}>
              {({ getFieldValue }) => (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ ...label, marginBottom: 8 }}>Occurrences count in frequency</div>
                  <div>{occurrenceCount(getFieldValue("frequency_type_id")) ?? "-"}</div>
                </div>
              )}
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="notes" label="Task Instructions">
          <Input.TextArea rows={2} />
        </Form.Item>
        <Form.Item name="is_seperately_charge" valuePropName="checked" style={{ marginBottom: 12 }}>
          <Checkbox>Is separately charged</Checkbox>
        </Form.Item>
        <Form.Item noStyle shouldUpdate={(prev, cur) => prev.is_seperately_charge !== cur.is_seperately_charge}>
          {({ getFieldValue }) => getFieldValue("is_seperately_charge") && (
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item name="charge_type_id" label="Charge Type" rules={[{ required: true, message: "Please select a charge type." }]}>
                  <Select showSearch optionFilterProp="children" placeholder="Select a charge type">
                    {chargeTypes.map((c) => <Option key={c.id} value={c.id}>{c.name}</Option>)}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="charge_amount" label="Charge Amount" rules={[{ required: true, message: "This field is required." }]}>
                  <InputNumber min={0} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
            </Row>
          )}
        </Form.Item>

        <Form.List name="reporting_fields">
          {(fields, { add, remove }) => (
            <>
              <div style={{ marginBottom: 8, fontSize: 13, color: "#5B6472" }}>Custom Reporting Fields</div>
              {fields.map(({ key, name, ...rest }) => (
                <Row gutter={8} key={key} align="middle" style={{ marginBottom: 8 }}>
                  <Col span={10}>
                    <Form.Item {...rest} name={[name, "label"]} rules={[{ required: true, message: "Please enter the label." }]} noStyle>
                      <Input placeholder="Field label" />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item {...rest} name={[name, "data_type_id"]} rules={[{ required: true, message: "Please enter the type." }]} noStyle>
                      <Select placeholder="Type">
                        {reportingTypes.map((t) => <Option key={t.id} value={t.id}>{t.name}</Option>)}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={4}>
                    <Form.Item {...rest} name={[name, "is_mandatory"]} valuePropName="checked" noStyle>
                      <Switch checkedChildren="Req" unCheckedChildren="Opt" />
                    </Form.Item>
                  </Col>
                  <Col span={2}>
                    <MinusCircleOutlined onClick={() => remove(name)} />
                  </Col>
                </Row>
              ))}
              <Button size="small" icon={<PlusOutlined />} onClick={() => add({ is_mandatory: false })}>Add Field</Button>
            </>
          )}
        </Form.List>
      </Form>
    </Modal>
  );
};

const PeriodicalConfig = () => {
  const [groups, setGroups] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [groupId, setGroupId] = useState(null);
  const [facilityId, setFacilityId] = useState(null);
  // The old app's Allocation screen only ever shows Group + Facility
  // dropdowns — Contract is resolved silently as the group's first contract
  // (fetchContractList(group_id), then contract_data[0].id, no UI for it),
  // and its Facility list is fetched straight from the group
  // (fetchFacilityList(group_id) — same managed_facilities_group endpoint
  // ConsolidateReport.js already uses, not the Contract-scoped one) —
  // confirmed by reading PeriodicalConfig/index.js's onGroupChange/onApplyClick
  // directly. Mirrored here instead of the 3-level FacilityCascade the other
  // screens use.
  const [contractId, setContractId] = useState(null);
  const [contractFacilityId, setContractFacilityId] = useState(null);
  const [facilityName, setFacilityName] = useState(null);
  const [applied, setApplied] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [assigned, setAssigned] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [occurrences, setOccurrences] = useState([]);
  const [reportingTypes, setReportingTypes] = useState([]);
  const [chargeTypes, setChargeTypes] = useState([]);
  const [assignModal, setAssignModal] = useState({ visible: false, catalogRow: null, editingRow: null, occurrenceId: null });

  useEffect(() => {
    (async () => {
      const [groupsRes, occRes, reportRes, chargeRes] = await Promise.all([
        getGroups(), getOccurrences(), getReportingTypes(), getChargeTypes(),
      ]);
      setGroups(!groupsRes.error && Array.isArray(groupsRes.result?.result) ? groupsRes.result.result : []);
      setOccurrences(!occRes.error && Array.isArray(occRes.result?.result) ? occRes.result.result : []);
      setReportingTypes(!reportRes.error && Array.isArray(reportRes.result?.result) ? reportRes.result.result : []);
      setChargeTypes(!chargeRes.error && Array.isArray(chargeRes.result?.result) ? chargeRes.result.result : []);
    })();
  }, []);

  const onGroupChange = useCallback(async (value) => {
    setGroupId(value);
    setFacilityId(null);
    setContractId(null);
    setFacilities([]);
    setApplied(false);
    if (!value) return;
    const [contractsRes, facilitiesRes] = await Promise.all([getContractsByGroup(value), getFacilitiesByGroup(value)]);
    const contracts = !contractsRes.error && Array.isArray(contractsRes.result?.result) ? contractsRes.result.result : [];
    setContractId(contracts[0]?.id ?? null);
    setFacilities(!facilitiesRes.error && Array.isArray(facilitiesRes.result?.result) ? facilitiesRes.result.result : []);
  }, []);

  const onFacilityChange = useCallback((value) => {
    setFacilityId(value);
    setApplied(false);
  }, []);

  const load = useCallback(async () => {
    if (!contractId || !contractFacilityId) return;
    setLoading(true);
    const [catalogRes, assignedRes] = await Promise.all([
      getContractPeriodicals(contractId),
      getContractFacilityPeriodicals(contractFacilityId),
    ]);
    setCatalog(!catalogRes.error && Array.isArray(catalogRes.result?.result) ? catalogRes.result.result : []);
    setAssigned(!assignedRes.error && Array.isArray(assignedRes.result?.result) ? assignedRes.result.result : []);
    setLoading(false);
  }, [contractId, contractFacilityId]);

  const onApply = useCallback(() => {
    if (!groupId || !facilityId) {
      message.error("Please select a group and facility first.");
      return;
    }
    const facility = facilities.find((f) => (f.facility_id ?? f.id) === facilityId);
    setContractFacilityId(facilityId);
    setFacilityName(facility?.facility_name ?? facility?.name ?? null);
    setSearch("");
    setApplied(true);
  }, [groupId, facilityId, facilities]);

  useEffect(() => { if (applied) load(); }, [applied, load]);

  const assignedPeriodicalIds = new Set(assigned.map((a) => a.periodical_id));
  // A to Z by name
  const available = catalog
    .filter((c) => !assignedPeriodicalIds.has(c.periodical_id))
    .sort((x, y) => String(x.name || "").localeCompare(String(y.name || ""), undefined, { sensitivity: "base" }));
  const searchText = search.trim().toLowerCase();
  const shown = searchText
    ? available.filter((c) => [c.name, c.frequency_name, c.description].some((v) => String(v || "").toLowerCase().includes(searchText)))
    : available;

  const handleRemove = useCallback((row) => {
    confirm({
      title: "Remove periodical",
      content: `Remove "${row.name}" from this facility's configuration?`,
      okText: "Remove",
      okType: "danger",
      onOk: async () => {
        const { error: wErr, result: wRes } = await isWorkingOnPeriodical(row.id);
        const workingCount = !wErr && Array.isArray(wRes?.result) ? wRes.result[0]?.count : 0;
        if (workingCount > 0) {
          message.error("This allocation cannot be deleted. Some tasks have already been completed.");
          return;
        }
        const { error, result } = await removeFacilityPeriodical(row.id);
        if (error) message.error(result?.message || "Failed to remove.");
        else { message.success("Removed."); load(); }
      },
    });
  }, [load]);

  return (
    <div>
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 16, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div>
            <div style={{ marginBottom: 4, fontSize: 13, color: "#5B6472" }}>Group</div>
            <Select showSearch optionFilterProp="children" placeholder="Select a group" style={{ width: 200 }} value={groupId} onChange={onGroupChange}>
              {groups.map((g) => <Option key={g.id} value={g.id}>{g.name}</Option>)}
            </Select>
          </div>
          <div>
            <div style={{ marginBottom: 4, fontSize: 13, color: "#5B6472" }}>Facility</div>
            <Select
              showSearch
              optionFilterProp="children"
              placeholder="Select a facility"
              style={{ width: 220 }}
              value={facilityId}
              onChange={onFacilityChange}
              disabled={!groupId}
              notFoundContent={groupId ? "No facilities for this group" : "Select a group first"}
            >
              {facilities.map((f) => <Option key={f.facility_id ?? f.id} value={f.facility_id ?? f.id}>{f.facility_name ?? f.name}</Option>)}
            </Select>
          </div>
          <Button type="primary" onClick={onApply}>Apply</Button>
        </div>
      </Card>

      {!applied ? (
        <Card><Empty description="Select a group and facility, then Apply, to configure its periodicals." /></Card>
      ) : (
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={8}>
            <Card title={`Contract Periodicals (${available.length})`} loading={loading} bodyStyle={{ padding: 0 }}>
              <div style={{ padding: "12px 16px", borderBottom: "1px solid #f0f0f0" }}>
                <Input.Search allowClear placeholder="Search periodicals" value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
              {/* Fixed height so a long catalogue scrolls inside the card. */}
              <div style={{ height: 560, overflowY: "auto" }}>
                <List
                  dataSource={shown}
                  locale={{ emptyText: searchText ? "No periodicals match your search." : "All contract periodicals are already assigned." }}
                  renderItem={(row) => (
                    <List.Item
                      style={{ padding: "8px 16px", cursor: "pointer" }}
                      onClick={() => setAssignModal({ visible: true, catalogRow: row, editingRow: null })}
                      actions={[<PlusOutlined key="assign" />]}
                    >
                      <List.Item.Meta
                        title={<span style={{ wordBreak: "break-word" }}>{row.name}</span>}
                        description={row.frequency_name ? `Default: ${row.frequency_name}` : row.description}
                      />
                    </List.Item>
                  )}
                />
              </div>
            </Card>
          </Col>
          <Col xs={24} lg={16}>
            <Card title="Frequency Master" loading={loading}>
              <Row gutter={[12, 12]}>
                {occurrences.map((occ) => {
                  const cellItems = assigned.filter((a) => a.occurrence_id === occ.id);
                  return (
                    <Col xs={24} md={12} xl={8} key={occ.id}>
                      <Card size="small" title={<span title={occ.name}>{occ.name}</span>} style={{ height: "100%" }}>
                        {cellItems.length === 0 ? (
                          <div style={{ fontSize: 12, color: "#bfbfbf" }}>No periodicals</div>
                        ) : (
                          cellItems.map((row) => (
                            <Tag
                              key={row.id}
                              closable
                              onClose={(e) => { e.preventDefault(); handleRemove(row); }}
                              onClick={() => setAssignModal({ visible: true, catalogRow: null, editingRow: row })}
                              color={row.is_separately_charge ? "gold" : "blue"}
                              title={row.name}
                              /* Long names wrap inside the cell instead of running past it. */
                              style={{ cursor: "pointer", marginBottom: 6, maxWidth: "100%", whiteSpace: "normal", wordBreak: "break-word", height: "auto", lineHeight: "18px", padding: "3px 8px", borderRadius: 8 }}
                            >
                              {row.name}
                            </Tag>
                          ))
                        )}
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            </Card>
          </Col>
        </Row>
      )}

      <AssignModal
        visible={assignModal.visible}
        catalogRow={assignModal.catalogRow}
        editingRow={assignModal.editingRow}
        occurrences={occurrences}
        reportingTypes={reportingTypes}
        chargeTypes={chargeTypes}
        contractFacilityId={contractFacilityId}
        facilityName={facilityName}
        onClose={() => setAssignModal({ visible: false, catalogRow: null, editingRow: null })}
        onSaved={load}
      />
    </div>
  );
};

export default memo(PeriodicalConfig);
